import { Component, createRef } from 'preact';
import * as THREE from 'three';
import { FOIL_VERTEX_SHADER, FOIL_FRAGMENT_SHADER } from '../utils/sorcery/foilShader.js';
import { FOIL_WHITE_ZONES } from '../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS, paramUniformName } from '../utils/sorcery/foilParams.js';
import { buildFoilUniforms, foilPlaceholderTexture, getFoilMatcap, loadFoilTextures } from '../utils/sorcery/foilMaterial.js';
import { isPhoneLayout } from '../utils/platform.js';

// Single-context WebGL foil scene: renders MANY foil cards as textured quads
// positioned in canvas-local pixel space, replacing the per-card FoilCanvas
// contexts (which raced the browser's ~16-context ceiling) and the buggy
// DOM-overlay FoilStage. The renderer setup (alpha, NoColorSpace texture +
// LinearSRGB output, transparent clear) is byte-identical to FoilCanvas so the
// look matches exactly; the only difference is one camera/scene drawing the
// whole visible grid instead of one quad.

// Shared texture cache, keyed by card art URL, at MODULE scope so it survives
// component unmount/remount (scroll virtualization mounts/unmounts this often)
// and is shared across instances. A card scrolling back into view re-uses its
// already-decoded GPU textures instantly instead of re-decoding. refs counts
// how many live quads reference a url; entries with refs===0 are LRU-eligible.
const _texCache = new Map();
// The cache is bounded by BYTES, not entry count: entries differ in size by
// an order of magnitude across displays (a phone's 512px-capped set vs a
// desktop's full 744×1040 maps), so a fixed entry cap either starves big
// displays or lets a phone's cache grow to hundreds of MB. the latter is
// exactly what got the tab jetsam-killed on iOS Safari. Phones get a third
// of the desktop budget: their capped decodes are small, so 32 MB still
// holds a full grid's worth, and iOS Safari's per-tab memory ceiling is a
// fraction of a desktop browser's.
const _TEX_CACHE_BUDGET_BYTES = (isPhoneLayout() ? 32 : 96) * 1024 * 1024;
let _texCacheBytes = 0;

// Approximate resident cost of a texture: RGBA at image dims + 1/3 mipmaps.
function _texBytes(tex) {
  const img = tex?.image;
  if (!img || !img.width) return 0;
  return img.width * img.height * 4 * 1.33;
}

// Camera distance = DOM tiles' CSS perspective:600px, so a hovered quad's tilt
// foreshortens identically to the legacy DOM pivot.
const FOIL_PERSPECTIVE_D = 600;
const _DEG = Math.PI / 180;
// Below this, an eased value is snapped to its target and easing is considered
// settled (so we stop rendering). Radians (~0.03deg) and scale/px units alike.
// sub-pixel / sub-perceptible, so snapping is visually identical to converging.
const _EASE_EPS = 0.0005;

function _acquireTexture(imageUrl, maxSize, onTex, onFoilMap, onDepthMap) {
  let entry = _texCache.get(imageUrl);
  if (entry) {
    entry.refs++;
    _texCache.delete(imageUrl);
    _texCache.set(imageUrl, entry); // bump to MRU
    if (entry.tex) onTex(entry.tex);
    if (entry.foilMap) onFoilMap(entry.foilMap);
    if (entry.depthMap) onDepthMap(entry.depthMap);
    return entry;
  }
  entry = { tex: null, foilMap: null, depthMap: null, refs: 1, bytes: 0 };
  const track = (tex) => {
    // The entry can be evicted while its decode is still in flight (quad
    // released + budget sweep). Counting a late arrival would inflate
    // _texCacheBytes forever (the eviction already subtracted this entry's
    // bytes) and leak the texture. Dispose it instead of tracking it.
    if (_texCache.get(imageUrl) !== entry) {
      const placeholder = foilPlaceholderTexture();
      if (tex && tex !== placeholder) tex.dispose?.();
      return;
    }
    const b = _texBytes(tex);
    entry.bytes += b;
    _texCacheBytes += b;
  };
  _texCache.set(imageUrl, entry);
  loadFoilTextures(imageUrl, {
    texColorSpace: THREE.NoColorSpace,
    // First acquire wins the decode size for as long as the entry lives.
    // All quads in one grid share a tile size, so this only matters across
    // surfaces. where sizes are close enough that the bucket matches.
    maxSize,
    onTex: (tex) => { entry.tex = tex; track(tex); onTex(tex); },
    onFoilMap: (tex) => { entry.foilMap = tex; track(tex); onFoilMap(tex); },
    onDepthMap: (tex) => { entry.depthMap = tex; track(tex); onDepthMap(tex); },
  });
  return entry;
}

function _releaseTexture(imageUrl) {
  const entry = _texCache.get(imageUrl);
  if (!entry) return;
  entry.refs--;
  if (entry.refs > 0) return;
  // refs hit 0: keep it (cheap re-mount) but evict the oldest free entries
  // while the cache sits over its byte budget.
  for (const [url, e] of _texCache) {
    if (_texCacheBytes <= _TEX_CACHE_BUDGET_BYTES) break;
    if (e.refs > 0) continue;
    e.tex?.dispose?.();
    const placeholder = foilPlaceholderTexture();
    if (e.foilMap && e.foilMap !== placeholder) e.foilMap.dispose?.();
    if (e.depthMap && e.depthMap !== placeholder) e.depthMap.dispose?.();
    _texCacheBytes -= e.bytes;
    _texCache.delete(url);
  }
}

export default class CardFoilScene extends Component {
  canvasRef = createRef();
  _byKey = new Map();
  _cards = [];

  componentDidMount() {
    this._cards = this.props.cards || [];
    try {
      this._init();
    } catch (err) {
      console.warn('[CardFoilScene] WebGL init failed:', err?.message || err);
      this._dispose();
    }
  }

  componentDidUpdate(prevProps) {
    // When a `cards` prop is supplied, this component is prop-driven (the
    // original contract). When it's omitted, the parent feeds quads imperatively
    // via setCards() each frame, so we must NOT clobber _cards from props here.
    if (this.props.cards !== undefined) this._cards = this.props.cards;
    if (!this.renderer) return;
    if (prevProps.width !== this.props.width || prevProps.height !== this.props.height) {
      this._resize();
    }
    if (this.props.cards !== undefined) this._reconcile();
  }

  componentWillUnmount() {
    this._dispose();
  }

  // Imperative per-frame feed: the parent's rAF reads scrollTop once and pushes
  // fresh quad positions here without a React render, so the canvas positions
  // and the DOM tile layer transform come from the SAME frame (no setState
  // jank, no desync). Reconciles the mesh set when the visible cards change;
  // the owned rAF consumes `_cards` for position/tilt every frame.
  setCards(cards) {
    const next = cards || [];
    // Mark the scene dirty only when the incoming quads actually differ from the
    // last fed array. At rest the parent feeds an identical array every frame
    // (same scrollTop -> identical x/y/etc), so this yields no change and the
    // frame() gate below skips renderer.render. Scroll / layout / the hand
    // overlay's live-transform feed all change these values -> dirty -> render.
    if (this._cardsDiffer(this._cards, next)) this._dirty = true;
    this._cards = next;
    if (this.renderer) this._reconcile(); // _reconcile sets _dirty on add/remove
  }

  // Per-card shallow compare across the fields that affect the rendered frame.
  _cardsDiffer(a, b) {
    if (a.length !== b.length) return true;
    for (let i = 0; i < a.length; i++) {
      const p = a[i];
      const c = b[i];
      if (
        p.key !== c.key ||
        p.x !== c.x || p.y !== c.y || p.w !== c.w || p.h !== c.h ||
        p.rotateX !== c.rotateX || p.rotateY !== c.rotateY || p.rotateZ !== c.rotateZ ||
        p.scale !== c.scale || p.nudgeX !== c.nudgeX || p.nudgeY !== c.nudgeY ||
        p.hovering !== c.hovering || p.cornerRadius !== c.cornerRadius ||
        p.zonesOn !== c.zonesOn || p.opacity !== c.opacity ||
        p.mx !== c.mx || p.my !== c.my || p.fromCenter !== c.fromCenter
      ) return true;
    }
    return false;
  }

  _init() {
    const canvas = this.canvasRef.current;
    // No MSAA on phones: the multisample buffer doubles this (viewport-sized)
    // canvas's GPU memory, and at phone dpr the geometric aliasing it hides is
    // sub-pixel anyway.
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: !isPhoneLayout(), premultipliedAlpha: false });
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    renderer.setClearColor(0x000000, 0);
    this.renderer = renderer;
    // Pixel ratio is set by _resize() below (via _fittedPixelRatio), which is the
    // only place that knows the canvas dimensions. This used to set its own ratio
    // here with a different expression than _resize, so the two disagreed.

    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); this._lost = true; }, false);
    canvas.addEventListener('webglcontextrestored', () => {
      this._lost = false;
      this._dirty = true; // restored context -> repaint
      for (const e of _texCache.values()) {
        if (e.tex) e.tex.needsUpdate = true;
        if (e.foilMap) e.foilMap.needsUpdate = true;
      }
    }, false);

    this.scene = new THREE.Scene();
    this._setCamera();
    this._resize();

    // CSS perspective(D) matrix (column vectors): maps (x,y,z,1) -> (x,y,z,1-z/D)
    // so the GPU perspective-divide reproduces a CSS `perspective:600px`. Baking
    // it into EACH card's matrix (translate-to-center · P · rotate) gives every
    // card its own perspective origin at its center, exactly like a per-tile CSS
    // `perspective`. Reused temp matrix avoids per-frame allocation.
    this._persp = new THREE.Matrix4().set(
      1, 0, 0, 0,
      0, 1, 0, 0,
      0, 0, 1, 0,
      0, 0, -1 / FOIL_PERSPECTIVE_D, 1,
    );
    this._tmpMat = new THREE.Matrix4();

    this._reconcile();
  }

  // Render one frame. Driven by the PARENT's single rAF (the grid calls
  // setCards() then frame() each tick) so the foil quads and the DOM tile-layer
  // transform are positioned from the SAME scrollTop in the SAME frame. They
  // can never desync (the old per-component rAF ran a frame behind, so foil
  // cards lagged the non-foil cards => the scroll micro-stutter). dt advances
  // uTime + eases the hover pivot.
  frame(dt) {
    if (!this.renderer || this._lost) return;
    const h = this.props.height || 0;
    // Determine if anything would change this frame. uTime is unused by the
    // shader, so an idle frame (no dirty input, no in-flight ease) is
    // pixel-identical to the last -> skip renderer.render but keep the rAF alive.
    // `_dirty` covers: setCards delivered different quads (scroll / layout / the
    // hand overlay's live-transform feed change x/y/tilt. And for `instant`
    // cards a changed value lands here, not in easing), reconcile add/remove,
    // texture-ready, resize, context-restore. `easing` covers a non-instant
    // hover pivot/scale/nudge still converging toward its target.
    let anyLifted = false;
    let easing = false;
    for (const card of this._cards) {
      if (card.hovering) anyLifted = true;
      const entry = this._byKey.get(card.key);
      if (!entry || !entry.texReady || card.instant) continue;
      const tRotX = -(card.rotateX || 0) * _DEG;
      const tRotY = (card.rotateY || 0) * _DEG;
      const tRotZ = -(card.rotateZ || 0) * _DEG;
      const tScale = card.scale || 1;
      if (
        Math.abs(tRotX - entry.curRotX) > _EASE_EPS ||
        Math.abs(tRotY - entry.curRotY) > _EASE_EPS ||
        Math.abs(tRotZ - entry.curRotZ) > _EASE_EPS ||
        Math.abs(tScale - entry.curScale) > _EASE_EPS ||
        Math.abs((card.nudgeX || 0) - entry.curNudgeX) > _EASE_EPS ||
        Math.abs((card.nudgeY || 0) - entry.curNudgeY) > _EASE_EPS
      ) { easing = true; break; }
    }
    // Z-index update is cheap + idempotent; keep it running every frame so the
    // hover lift/lower still tracks even on a skipped (non-rendered) frame.
    const canvas0 = this.canvasRef.current;
    if (canvas0) {
      const z = anyLifted ? '3' : '1';
      if (canvas0.style.zIndex !== z) canvas0.style.zIndex = z;
    }
    if (!this._dirty && !easing) return; // nothing changed -> skip the redraw

    for (const card of this._cards) {
      const entry = this._byKey.get(card.key);
      if (!entry) continue;
      const { mesh, uniforms } = entry;
      // Don't draw the foil (zone mask boxes) until the card texture is ready.
      if (!entry.texReady) { mesh.visible = false; continue; }
      mesh.visible = true;
      // Ease the hover pivot (rotateX/Y) + lift (scale) toward target so the
      // card pivots in smoothly like the legacy CSS-transition hover. Targets
      // are 0/0/1 at rest. Scene is y-up (cy = h - domY); CSS rotateX is in
      // y-down space, so negate X. rotateY keeps its sign.
      const tRotX = -(card.rotateX || 0) * _DEG;
      const tRotY = (card.rotateY || 0) * _DEG;
      // Negate Z too: the scene is y-up (y flipped vs the DOM), which reverses
      // in-plane rotation sense, so CSS clockwise rotate(90deg) needs -90 here.
      const tRotZ = -(card.rotateZ || 0) * _DEG;
      const tScale = card.scale || 1;
      // `instant` = the caller already feeds a LIVE, externally-animated
      // transform every frame (e.g. the hand overlay reading the card's
      // CSS-animated rotation/scale), so easing here would double-smooth and
      // make the foil trail the card (a lagging, mis-scaled "ghost" during the
      // hover spring). k=1 applies the read value directly. Default k=0.25 eases
      // for callers that feed jump-to-target values (the grid hover pivot).
      const k = card.instant ? 1 : 0.25;
      entry.curRotX += (tRotX - entry.curRotX) * k;
      entry.curRotY += (tRotY - entry.curRotY) * k;
      entry.curRotZ += (tRotZ - entry.curRotZ) * k;
      entry.curScale += (tScale - entry.curScale) * k;
      // Keep-in-view nudge for rotated sites (same px the DOM path translates).
      // DOM nudgeY is y-down; scene is y-up so subtract it.
      entry.curNudgeX += ((card.nudgeX || 0) - entry.curNudgeX) * k;
      entry.curNudgeY += ((card.nudgeY || 0) - entry.curNudgeY) * k;
      // Snap to target within epsilon so a settled card stops re-flagging easing
      // (otherwise the asymptotic lerp would render forever at micro amplitudes).
      if (Math.abs(tRotX - entry.curRotX) <= _EASE_EPS) entry.curRotX = tRotX;
      if (Math.abs(tRotY - entry.curRotY) <= _EASE_EPS) entry.curRotY = tRotY;
      if (Math.abs(tRotZ - entry.curRotZ) <= _EASE_EPS) entry.curRotZ = tRotZ;
      if (Math.abs(tScale - entry.curScale) <= _EASE_EPS) entry.curScale = tScale;
      if (Math.abs((card.nudgeX || 0) - entry.curNudgeX) <= _EASE_EPS) entry.curNudgeX = card.nudgeX || 0;
      if (Math.abs((card.nudgeY || 0) - entry.curNudgeY) <= _EASE_EPS) entry.curNudgeY = card.nudgeY || 0;
      // Build mesh.matrix = T(center) · P · Rx · Ry · [Rz] · S. The perspective
      // sits BETWEEN the center-translate and the rotation, so its origin is the
      // card's own center -> per-card CSS perspective (not a shared one).
      const cx = card.x + card.w / 2 + entry.curNudgeX;
      const cy = h - (card.y + card.h / 2) - entry.curNudgeY;
      const M = mesh.matrix;
      const R = this._tmpMat;
      M.makeTranslation(cx, cy, 0);
      M.multiply(this._persp);
      R.makeRotationX(entry.curRotX); M.multiply(R);
      R.makeRotationY(entry.curRotY); M.multiply(R);
      R.makeRotationZ(entry.curRotZ); M.multiply(R);
      R.makeScale(card.w * entry.curScale, card.h * entry.curScale, 1); M.multiply(R);
      mesh.matrixWorldNeedsUpdate = true;
      // Hovered card draws last so it sits above its neighbors. Use the hover
      // flag (immediate) rather than the eased transform so there's no lag.
      mesh.renderOrder = card.hovering ? 1 : 0;
      uniforms.uTilt.value.set((card.mx ?? 50) / 100 - 0.5, (card.my ?? 50) / 100 - 0.5);
      uniforms.uFromCenter.value = card.fromCenter || 0;
      uniforms.uOpacity.value = card.opacity ?? 1;
      uniforms.uTime.value += dt;
    }
    this.renderer.render(this.scene, this.camera);
    this._dirty = false;
  }

  _setCamera() {
    const w = this.props.width || 1;
    const h = this.props.height || 1;
    // Orthographic pixel-space, y-UP (top=h, bottom=0). The camera is ortho so it
    // adds NO global perspective; each card's own matrix carries its CSS
    // perspective (see loop), giving every card its own vanishing point at its
    // center. Matching per-tile CSS `perspective:600px`. A shared perspective
    // camera would instead vanish all cards toward screen center.
    if (!this.camera) this.camera = new THREE.OrthographicCamera(0, w, h, 0, -2000, 2000);
    else { this.camera.left = 0; this.camera.right = w; this.camera.top = h; this.camera.bottom = 0; }
    this.camera.updateProjectionMatrix();
  }

  // Largest pixel ratio whose backing store still fits what the GL context will
  // actually allocate.
  //
  // This canvas is NOT viewport-sized: it spans the whole windowed row range
  // (windowRows * rowHeight + headroom) so it can ride native scroll on the
  // compositor. That height grows with both the row height and BUFFER_ROWS, and
  // every dimension here is cqw-derived. So on a large display the requested
  // backing store is several times what it is on a laptop. Ask for more than the
  // implementation will give and the browser hands back a SMALLER drawing buffer
  // while the CSS box keeps its requested size: everything drawn is then
  // magnified to fill the box, which is exactly the "foil cards are huge and in
  // the wrong place, normal tiles are fine" failure. Clamping the ratio ourselves
  // keeps the buffer within limits AND keeps Three's viewport in agreement with
  // it, so geometry stays correct and only sharpness gives way.
  _fittedPixelRatio(w, h) {
    // Phones cap harder: dpr-3 screens can't show the extra sharpness at
    // grid-tile size, and the backing store counts against iOS Safari's
    // per-tab memory budget (exceeding it reload-loops the whole page).
    const dpr = Math.min(window.devicePixelRatio || 1, isPhoneLayout() ? 1.5 : 2);
    const gl = this.renderer?.getContext?.();
    if (!gl) return dpr;
    const dims = gl.getParameter(gl.MAX_VIEWPORT_DIMS) || [];
    const maxDim = Math.min(
      gl.getParameter(gl.MAX_TEXTURE_SIZE) || Infinity,
      dims[0] || Infinity,
      dims[1] || Infinity,
    );
    if (!Number.isFinite(maxDim) || maxDim <= 0) return dpr;
    // Leave a little headroom under the hard limit. Some drivers fail slightly
    // before it, and a silently-clamped buffer is far worse than a soft one.
    const budget = maxDim * 0.95;
    // Area budget on top of the per-dimension one. At canvas scale ~1.75 this
    // canvas is ~2270 x ~5950 CSS px; at ratio 2 that is a 4534 x 11900 backing
    // store. ~216 MB, and `antialias: true` multiplies that again for the
    // multisample buffer. Both dimensions are legal but the allocation is not,
    // and a refused allocation degrades exactly the same way (smaller buffer,
    // same CSS box, magnified content). 32 MP is far more than a grid of ~390px
    // tiles can resolve.
    const areaRatio = Math.sqrt((isPhoneLayout() ? 12e6 : 32e6) / Math.max(w * h, 1));
    return Math.max(0.5, Math.min(dpr, budget / Math.max(w, 1), budget / Math.max(h, 1), areaRatio));
  }

  _resize() {
    if (!this.renderer) return;
    const w = this.props.width || 1;
    const h = this.props.height || 1;
    this.renderer.setPixelRatio(this._fittedPixelRatio(w, h));
    this.renderer.setSize(w, h, false);
    this._setCamera();
    this._dirty = true; // buffer/camera changed -> repaint
    // TEMPORARY DIAGNOSTIC. Remove with the foil-quad mismatch fix. The quad
    // math checks out on paper, so the remaining suspect is the drawing buffer:
    // if the requested backing store exceeds what the GL implementation will
    // allocate, the browser silently hands back a SMALLER buffer while the CSS
    // box keeps its requested size, and everything drawn is magnified to fill it.
    // Compare requested vs actual to see that immediately.
    const canvas = this.canvasRef.current;
    const gl = this.renderer.getContext?.();
    console.log('[foil-canvas]', JSON.stringify({
      props: [w, h],
      pixelRatio: this.renderer.getPixelRatio(),
      requestedBuffer: [Math.floor(w * this.renderer.getPixelRatio()), Math.floor(h * this.renderer.getPixelRatio())],
      actualBuffer: canvas ? [canvas.width, canvas.height] : null,
      drawingBuffer: gl ? [gl.drawingBufferWidth, gl.drawingBufferHeight] : null,
      cssBox: canvas ? [canvas.style.width, canvas.style.height] : null,
      maxTexture: gl ? gl.getParameter(gl.MAX_TEXTURE_SIZE) : null,
      maxViewport: gl ? Array.from(gl.getParameter(gl.MAX_VIEWPORT_DIMS)) : null,
      camera: this.camera ? [this.camera.left, this.camera.right, this.camera.top, this.camera.bottom] : null,
    }));
  }

  _makeEntry(card) {
    const zones = card.zones || FOIL_WHITE_ZONES;
    const uniforms = buildFoilUniforms({ zones });
    for (const k of Object.keys(DEFAULT_FOIL_PARAMS)) {
      uniforms[paramUniformName(k)].value = DEFAULT_FOIL_PARAMS[k];
    }
    // Per-scene shader param overrides. Used by the deck-builder grid to
    // disable the three priciest shader features (POM loop, 3-octave glint,
    // sub-pixel sparkle) that are imperceptible at tile size but dominate the
    // fragment cost during scroll. The remaining stack (GGX, env, iridescence,
    // thin-film, emboss, rest holo) is what defines the foil look at any scale.
    const overrides = this.props.paramOverrides;
    if (overrides) {
      for (const k of Object.keys(overrides)) {
        const u = uniforms[paramUniformName(k)];
        if (u) u.value = overrides[k];
      }
    }
    getFoilMatcap((tex) => { uniforms.uMatcap.value = tex; });
    const material = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: FOIL_VERTEX_SHADER,
      fragmentShader: FOIL_FRAGMENT_SHADER,
      transparent: true,
      // No depth write/test: these are overlapping transparent quads, and a
      // perspective-tilted card has parts at z<0 that would otherwise fail the
      // depth test against a neighbor's z=0 depth and be occluded BY the neighbor
      // (the "hovered card sinks below its neighbors" bug). renderOrder alone
      // controls stacking -> the hovered card (renderOrder 1) is always on top.
      depthTest: false,
      depthWrite: false,
    });
    uniforms.uCornerRadius.value = card.cornerRadius || 0;
    uniforms.uQuadAspect.value = card.h / card.w;
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
    // We drive mesh.matrix directly each frame (T·P·R·S with baked CSS
    // perspective), so disable PRS-derived matrix recomputation.
    mesh.matrixAutoUpdate = false;
    // Hidden until the card texture loads. Otherwise the foil zone mask (name
    // plate + text-box rects) renders as bare metallic boxes on an empty card.
    mesh.visible = false;
    this.scene.add(mesh);

    const entry = {
      mesh, material, uniforms,
      imageUrl: card.imageUrl,
      zones: card.zones,
      cornerRadius: card.cornerRadius || 0,
      lastW: card.w, lastH: card.h,
      curRotX: 0, curRotY: 0, curRotZ: 0, curScale: 1, curNudgeX: 0, curNudgeY: 0,
      texReady: false,
    };
    _acquireTexture(
      card.imageUrl,
      this._texMaxSize(card),
      (tex) => {
        uniforms.uTex.value = tex;
        if (tex.image) uniforms.uTexel.value.set(1 / tex.image.width, 1 / tex.image.height);
        entry.texReady = true;
        this._dirty = true; // card newly drawable
        this._applyObjectCover(entry);
      },
      (tex) => { uniforms.uFoilMap.value = tex; uniforms.uHasFoilMap.value = 1; this._dirty = true; },
      (tex) => { uniforms.uDepthMap.value = tex; uniforms.uHasDepthMap.value = 1; this._dirty = true; },
    );
    return entry;
  }

  // Decode cap for this quad's textures: the quad's longer edge in device px
  // with 25% tilt/zoom headroom, bucketed to 256 steps so cache entries stay
  // shareable across near-identical tile sizes. A ~390px grid tile resolves
  // nothing past this; the 744×1040 foilmap decoded in full for it is waste.
  _texMaxSize(card) {
    const pr = this.renderer?.getPixelRatio?.() || 1;
    const long = Math.max(card.w || 0, card.h || 0) * pr;
    if (!long) return 1024;
    return Math.min(1024, Math.max(256, Math.ceil((long * 1.25) / 256) * 256));
  }

  // Center-crop the card art into its quad (object-cover), matching FoilCanvas
  // exactly: compare texture aspect to quad aspect and shrink+offset uv on the
  // longer axis. Recomputed when the texture loads or the quad's w/h changes.
  _applyObjectCover(entry) {
    const img = entry.uniforms.uTex.value?.image;
    if (!img || !img.width || !img.height || !entry.lastW || !entry.lastH) return;
    const texAspect = img.width / img.height;
    const quadAspect = entry.lastW / entry.lastH;
    let usX = 1, usY = 1, uoX = 0, uoY = 0;
    if (texAspect > quadAspect) {
      usX = quadAspect / texAspect;
      uoX = (1 - usX) / 2;
    } else if (texAspect < quadAspect) {
      usY = texAspect / quadAspect;
      uoY = (1 - usY) / 2;
    }
    entry.uniforms.uUvScale.value.set(usX, usY);
    entry.uniforms.uUvOffset.value.set(uoX, uoY);
  }

  _reconcile() {
    const seen = new Set();
    for (const card of this._cards) {
      seen.add(card.key);
      let entry = this._byKey.get(card.key);
      if (!entry) {
        entry = this._makeEntry(card);
        this._byKey.set(card.key, entry);
        this._dirty = true; // mesh added
        continue;
      }
      if (card.zones !== entry.zones) {
        const zones = card.zones || FOIL_WHITE_ZONES;
        const z0 = zones[0];
        const z1 = zones[1] || z0;
        entry.uniforms.uZone0.value.set(z0.x, z0.y, z0.w, z0.h);
        entry.uniforms.uZone1.value.set(z1.x, z1.y, z1.w, z1.h);
        entry.zones = card.zones;
        this._dirty = true; // zone mask changed
      }
      // Image swapped under a stable key (e.g. a two-sided card flipped
      // front<->back). Re-acquire the texture; the rAF loop re-hides the mesh
      // until texReady flips back true, so no bare-metal flash.
      if (card.imageUrl !== entry.imageUrl) {
        _releaseTexture(entry.imageUrl);
        entry.imageUrl = card.imageUrl;
        entry.texReady = false;
        entry.uniforms.uHasFoilMap.value = 0;
        entry.uniforms.uHasDepthMap.value = 0;
        _acquireTexture(
          card.imageUrl,
          this._texMaxSize(card),
          (tex) => {
            entry.uniforms.uTex.value = tex;
            if (tex.image) entry.uniforms.uTexel.value.set(1 / tex.image.width, 1 / tex.image.height);
            entry.texReady = true;
            this._dirty = true; // swapped card newly drawable
            this._applyObjectCover(entry);
          },
          (tex) => { entry.uniforms.uFoilMap.value = tex; entry.uniforms.uHasFoilMap.value = 1; this._dirty = true; },
          (tex) => { entry.uniforms.uDepthMap.value = tex; entry.uniforms.uHasDepthMap.value = 1; this._dirty = true; },
        );
      }
      if (card.w !== entry.lastW || card.h !== entry.lastH) {
        entry.lastW = card.w;
        entry.lastH = card.h;
        entry.uniforms.uQuadAspect.value = card.h / card.w;
        if (entry.texReady) this._applyObjectCover(entry);
        this._dirty = true; // quad size changed
      }
      if ((card.cornerRadius || 0) !== entry.cornerRadius) {
        entry.cornerRadius = card.cornerRadius || 0;
        entry.uniforms.uCornerRadius.value = entry.cornerRadius;
        this._dirty = true; // corner radius changed
      }
    }
    for (const [key, entry] of this._byKey) {
      if (seen.has(key)) continue;
      this.scene.remove(entry.mesh);
      entry.mesh.geometry.dispose();
      entry.material.dispose();
      _releaseTexture(entry.imageUrl);
      this._byKey.delete(key);
      this._dirty = true; // mesh removed
    }
  }

  _dispose() {
    if (this._raf) cancelAnimationFrame(this._raf);
    for (const [, entry] of this._byKey) {
      this.scene?.remove(entry.mesh);
      entry.mesh.geometry.dispose();
      entry.material.dispose();
      _releaseTexture(entry.imageUrl);
    }
    this._byKey.clear();
    // forceContextLoss releases the WebGL context immediately; dispose() alone
    // leaves it alive until GC, accumulating zombie contexts toward the
    // browser's ~16-context ceiling (see FoilCanvas._dispose).
    this.renderer?.dispose();
    this.renderer?.forceContextLoss?.();
    this.renderer = null;
  }

  // Imperative: stamp a CSS transform onto the canvas element. Used by the grid
  // to ride scroll between scene re-renders. The canvas extends past the
  // viewport vertically (topInset > 0), and translating the canvas via the
  // compositor moves its already-drawn foil quads with the scroll at zero
  // shader cost. Reset to identity when the next re-render lands the quads at
  // their new reference positions.
  setCanvasTransform(transform) {
    const canvas = this.canvasRef.current;
    if (canvas) canvas.style.transform = transform || '';
  }

  render() {
    // The drawing buffer (width/height props) can extend beyond the parent by
    // `leftInset` px (horizontal) and `topInset` px (vertical) so a hovered
    // card lifting/tilting over the grid edge isn't clipped at the canvas
    // boundary AND so the canvas can be CSS-translated vertically to follow
    // scroll without leaving uncovered strips. `inset` is the legacy symmetric
    // shorthand. CSS px == buffer px (no squish).
    const mLeft = this.props.leftInset ?? this.props.inset ?? 0;
    const mTop = this.props.topInset ?? this.props.inset ?? 0;
    return (
      <canvas
        ref={this.canvasRef}
        style={{ position: 'absolute', left: `${-mLeft}px`, top: `${-mTop}px`, width: `${this.props.width || 0}px`, height: `${this.props.height || 0}px`, pointerEvents: 'none', zIndex: 1 }}
      />
    );
  }
}
